import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HubTitle } from "@/components/hub/HubParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DatabaseZap, ShieldCheck, AlertTriangle } from "lucide-react";

const err = (e, f) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);

function Table({ rows, cols, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="border-b border-border text-left">{cols.map((c) => <th key={c.k} className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{c.h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-border/50">{cols.map((c) => <td key={c.k} className={`px-3 py-1.5 ${c.mono ? "font-mono text-xs" : ""}`}>{r[c.k]}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function Validation({ v }) {
  const row = (obj) => Object.entries(obj).map(([k, val]) => ({ k, val: typeof val === "number" ? val.toLocaleString() : String(val), bad: typeof val === "number" && val !== 0 }));
  return (
    <div className="space-y-3" data-testid="reset-validation">
      <p className={`text-lg font-bold ${v.status === "PASS" ? "text-signal-green" : "text-signal-red"}`} data-testid="reset-validation-status">Validation: {v.status}</p>
      <div className="grid md:grid-cols-3 gap-3 text-xs font-mono">
        {[["Stock buckets (must be 0)", v.stock, true], ["Transactions (must be 0)", v.transactions, true], ["Preserved (must be > 0)", v.preserved, false]].map(([t, o, zero]) => (
          <div key={t} className="bg-card border border-border rounded-lg p-3 space-y-1">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{t}</p>
            {row(o).map((r) => <div key={r.k} className="flex justify-between"><span>{r.k}</span><b className={zero && r.bad ? "text-signal-red" : ""}>{r.val}</b></div>)}
          </div>))}
      </div>
    </div>
  );
}

export default function DataReset() {
  const [plan, setPlan] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const load = () => { api.get("/admin/data-reset/plan").then((r) => setPlan(r.data)).catch((e) => toast.error(err(e, "Failed to load plan"))); api.get("/admin/data-reset/history").then((r) => setHistory(r.data)).catch(() => {}); };
  useEffect(load, []);

  const run = async () => {
    setRunning(true);
    try { const r = await api.post("/admin/data-reset/execute", { confirm }); setReport(r.data); setConfirm(""); toast.success(`Reset done — backup ${r.data.backup_id}`); load(); }
    catch (e) { toast.error(err(e, "Reset failed")); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-5" data-testid="data-reset-page">
      <HubTitle icon={DatabaseZap} title="Operational Data Reset" subtitle="Database-level wipe of ALL transactional data · masters, users, roles, RBAC and settings are preserved · full snapshot taken first" />
      {report && (
        <div className="bg-card border border-signal-green/40 rounded-lg p-4 space-y-3" data-testid="reset-report">
          <p className="font-bold">Reset report · Backup ID <span className="font-mono text-primary" data-testid="reset-backup-id">{report.backup_id}</span> · {new Date(report.at).toLocaleString()} · by {report.by}</p>
          <p className="text-sm">Collections reset: <b>{report.collections_reset}</b> · Total records reset: <b data-testid="reset-total">{report.total_records_reset.toLocaleString()}</b> · Snapshot records: {report.backup_records.toLocaleString()}</p>
          <Table testid="reset-by-group" cols={[{ k: "g", h: "Module" }, { k: "n", h: "Records reset" }]} rows={Object.entries(report.by_group).map(([g, n]) => ({ g, n: n.toLocaleString() }))} />
          <Validation v={report.validation} />
        </div>
      )}
      {plan && (
        <>
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-signal-red flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Will be WIPED — {plan.total_wipe_records.toLocaleString()} records across {plan.wipe.length} collections</p>
              <Table testid="reset-wipe-table" cols={[{ k: "group", h: "Module" }, { k: "collection", h: "Collection", mono: true }, { k: "records", h: "Records" }]} rows={plan.wipe} />
              <p className="text-[10px] font-mono uppercase tracking-widest text-signal-amber mt-3">Partial reset (master kept, values zeroed)</p>
              <Table testid="reset-partial-table" cols={[{ k: "collection", h: "Collection", mono: true }, { k: "action", h: "Action" }, { k: "records", h: "Affected" }]} rows={plan.partial} />
            </div>
            <div className="space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-signal-green flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Preserved (NOT touched)</p>
              <Table testid="reset-preserve-table" cols={[{ k: "collection", h: "Collection", mono: true }, { k: "records", h: "Records" }]} rows={plan.preserve} />
            </div>
          </div>
          <div className="bg-card border border-signal-red/40 rounded-lg p-4 space-y-3" data-testid="reset-confirm-block">
            <p className="text-sm">This permanently deletes the data listed above from the <b>connected database</b>. A full snapshot is stored in <code>reset_backups</code> (Backup ID) before any deletion. Type <b className="font-mono">RESET</b> to confirm.</p>
            <div className="flex gap-2 max-w-md">
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type RESET" data-testid="reset-confirm-input" />
              <Button disabled={confirm !== "RESET" || running} onClick={run} className="bg-signal-red hover:bg-signal-red/90 text-white font-bold" data-testid="reset-execute-btn">{running ? "Resetting…" : "EXECUTE RESET"}</Button>
            </div>
          </div>
        </>
      )}
      {history.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Previous resets</p>
          <Table testid="reset-history" cols={[{ k: "backup_id", h: "Backup ID", mono: true }, { k: "at", h: "When", mono: true }, { k: "by", h: "By" }, { k: "total_records_reset", h: "Records" }, { k: "status", h: "Validation" }]}
            rows={history.map((h) => ({ ...h, at: new Date(h.at).toLocaleString(), status: h.validation?.status }))} />
        </div>
      )}
    </div>
  );
}
